/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { type ReactNode, useMemo, useState } from 'react';

import {
  type ApiScan,
  getAudienceDocIncludePhotos,
  getAudienceDocPhotosOnly,
  getAudienceDocResidentResponsibleOnly,
  getAudienceDocShowAmounts,
  getAudienceDocShowOnlyChangeOrderChanges,
  type ScopeReportLineItem,
} from '@kaiizen/kai-pro-api';
import {
  ScanEditingContextProvider,
  useAudienceDocShareLinkMutators,
  useAudienceDocShareLinkStatus,
  useScanEditingContextSlice,
  useScopeReportData,
  useScopeReportDataCore,
  useTaskModifierDisplayNames,
} from '@kaiizen/kai-pro-api-react-state';
import { computeDiff, filterChangeOrderLineItems } from '@kaiizen/kai-reports';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { useToast } from '@/Client/State';
import { styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily, styleguideLetterSpacing, styleguideSpacing } from '@kaiizen/kai-shiki/core';
import { useAudienceDocShareLinkReplacementConfirmation } from '../../useAudienceDocShareLinkReplacementConfirmation';
import { formatArtifactDate } from './FocusedArtifactPanelLib';
import { HistoricalScopeArtifactDocument } from './HistoricalScopeArtifactDocument';
import {
  buildHistoricalArtifactPhotoGalleryGroups,
  getHistoricalArtifactTaskBaselineAmounts,
  getScanWithUpdatedHistoricalArtifactTaskAmount,
  type HistoricalArtifactTask,
} from './HistoricalScopeArtifactDocumentLib';

const COLORS = styleguideColors;
const FONT_FAMILY = styleguideFontFamily;
const FONT_SIZE = styleguideFontSize;
const FONT_WEIGHT = styleguideFontWeight;
const LETTER_SPACING = styleguideLetterSpacing;
const SPACING = styleguideSpacing;

type AudienceDocFilterSettings = {
  includePhotos: boolean;
  photosOnly: boolean;
  residentResponsibleOnly: boolean;
  showAmounts: boolean;
  showOnlyChangeOrderChanges: boolean;
};

const FILTER_STRIP_STYLE = css`
  label: AUDIENCE_DOC_FILTER_STRIP_STYLE;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.s3}px;
  padding: ${SPACING.s4}px ${SPACING.s5}px;
  border-bottom: 1px solid ${COLORS.stroke};
  background: ${COLORS.surfaceMuted};

  &::after {
    position: absolute;
    bottom: -${SPACING.s2}px;
    left: 50%;
    width: ${SPACING.s4}px;
    height: ${SPACING.s4}px;
    border-right: 1px solid ${COLORS.stroke};
    border-bottom: 1px solid ${COLORS.stroke};
    background: ${COLORS.surfaceMuted};
    content: '';
    transform: translateX(-50%) rotate(45deg);
  }

  @media print {
    display: none;
  }
`;

const FILTER_CAPTION_STYLE = css`
  label: AUDIENCE_DOC_FILTER_CAPTION_STYLE;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${SPACING.s2}px ${SPACING.s3}px;
`;

const FILTER_KICKER_STYLE = css`
  label: AUDIENCE_DOC_FILTER_KICKER_STYLE;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const FILTER_HELPER_STYLE = css`
  label: AUDIENCE_DOC_FILTER_HELPER_STYLE;
  color: ${COLORS.textSecondary};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-style: italic;
  line-height: 1.4;
`;

const LIVE_LINK_NOTE_STYLE = css`
  label: AUDIENCE_DOC_LIVE_LINK_NOTE_STYLE;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: ${SPACING.s2}px ${SPACING.s3}px;
  border-left: 3px solid ${COLORS.accent};
  padding: ${SPACING.s2}px ${SPACING.s3}px;
  background: ${COLORS.selectedWash};
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  line-height: 1.4;
`;

const LIVE_LINK_NOTE_LABEL_STYLE = css`
  label: AUDIENCE_DOC_LIVE_LINK_NOTE_LABEL_STYLE;
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const FILTER_CONTROLS_STYLE = css`
  label: AUDIENCE_DOC_FILTER_CONTROLS_STYLE;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.s3}px ${SPACING.s5}px;
`;

const FILTER_CHECKBOX_LABEL_STYLE = css`
  label: AUDIENCE_DOC_FILTER_CHECKBOX_LABEL_STYLE;
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.s2}px;
  color: ${COLORS.ink};
  cursor: pointer;
`;

const FILTER_CHECKBOX_STYLE = css`
  label: AUDIENCE_DOC_FILTER_CHECKBOX_STYLE;
  appearance: none;
  width: ${SPACING.s3}px;
  height: ${SPACING.s3}px;
  margin: 0;
  border: 1px solid ${COLORS.ink};
  background: ${COLORS.white};
  cursor: pointer;

  &:checked {
    box-shadow: inset 0 0 0 3px ${COLORS.accent};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const FILTER_LABEL_STYLE = css`
  label: AUDIENCE_DOC_FILTER_LABEL_STYLE;
  color: ${COLORS.ink};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const TOOLBAR_META_STYLE = css`
  label: AUDIENCE_DOC_TOOLBAR_META_STYLE;
  color: ${COLORS.textTertiary};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
`;

const SHARE_BUTTON_STYLE = css`
  label: AUDIENCE_DOC_SHARE_BUTTON_STYLE;
  flex: 0 0 auto;
  border: 1px solid ${COLORS.ink};
  padding: ${SPACING.s2}px ${SPACING.s3}px;
  background: ${COLORS.ink};
  color: ${COLORS.white};
  font-family: ${FONT_FAMILY.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${LETTER_SPACING.monoCaption};
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${COLORS.textSecondary};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const getAudienceDocFilterSettings = (
  audienceDoc: ApiScan,
  baseArtifact?: ApiScan,
): AudienceDocFilterSettings => ({
  includePhotos: getAudienceDocIncludePhotos(audienceDoc),
  photosOnly: getAudienceDocPhotosOnly(audienceDoc),
  residentResponsibleOnly: getAudienceDocResidentResponsibleOnly(audienceDoc),
  showAmounts: getAudienceDocShowAmounts(audienceDoc),
  showOnlyChangeOrderChanges: getAudienceDocShowOnlyChangeOrderChanges(audienceDoc, baseArtifact?.scanType),
});

const filterAudienceDocLineItems = <T extends ScopeReportLineItem>(
  scopeLineItems: T[],
  settings: AudienceDocFilterSettings,
) => scopeLineItems.filter(scopeLineItem => !settings.residentResponsibleOnly || scopeLineItem.isTenantRespLineItem);

const NO_SCOPE_LINE_ITEMS: ScopeReportLineItem[] = [];

/**
 * Resolves change-order line-item ids by diffing this copy against the change order's
 * fork baseline, the same comparison the share link renders.
 */
const ChangeOrderScopeLineItemIds = ({
  audienceDocScopeLineItems,
  children,
}: {
  audienceDocScopeLineItems: ScopeReportLineItem[];
  children: (scopeLineItemIds: ReadonlySet<string>) => ReactNode;
}) => {
  const { reportData: baselineReportData } = useScopeReportDataCore(true);
  const scopeLineItemIds = useMemo(
    () =>
      new Set(
        baselineReportData
          ? filterChangeOrderLineItems(computeDiff(audienceDocScopeLineItems, baselineReportData.scopeLineItems))
            .map(scopeLineItem => scopeLineItem.lineItemId)
          : [],
      ),
    [audienceDocScopeLineItems, baselineReportData],
  );

  return children(scopeLineItemIds);
};

/** Editable internal preview for the recipient-facing audience document. */
export const AudienceDocArtifactDocument = ({
  audienceDoc,
  baseArtifact,
  basedOnTitle,
  canManageShare,
  onBack,
}: {
  audienceDoc: ApiScan;
  /** Parent scope version this copy was minted from. */
  baseArtifact?: ApiScan;
  basedOnTitle: string;
  canManageShare: boolean;
  onBack: () => void;
}) => {
  const { showErrorToast, showToast } = useToast();
  const confirmAudienceDocShareLinkReplacement = useAudienceDocShareLinkReplacementConfirmation();
  const { reportData, diffReportData } = useScopeReportData();
  const modifierLabels = useTaskModifierDisplayNames();
  const { scanDataMutators, scanMutators, scanRoot } = useScanEditingContextSlice([
    'scanDataMutators',
    'scanMutators',
    'scanRoot',
  ]);
  const [settings, setSettings] = useState(() => getAudienceDocFilterSettings(audienceDoc, baseArtifact));
  const [pendingSettingsSaveCount, setPendingSettingsSaveCount] = useState(0);
  const { shareLinkStatus, refetch } = useAudienceDocShareLinkStatus(
    audienceDoc.id,
    { enabled: canManageShare },
  );
  const {
    createAudienceDocShareLink,
    isCreatingAudienceDocShareLink,
  } = useAudienceDocShareLinkMutators();
  const hasActiveLink = !!shareLinkStatus?.hasActiveLink;
  const isChangeOrderCopy = baseArtifact?.scanType === 'changeRequest';
  const isSavingSettings = pendingSettingsSaveCount > 0;
  const updateSettings = async (updates: Partial<AudienceDocFilterSettings>) => {
    if (!canManageShare) return false;
    const previousSettings = settings;
    const nextSettings = { ...settings, ...updates };
    setSettings(nextSettings);
    setPendingSettingsSaveCount(currentCount => currentCount + 1);
    try {
      await scanMutators.update(audienceDoc.id, {
        customerMetadata: {
          ...(updates.includePhotos === undefined
            ? {}
            : { audienceDocIncludePhotos: updates.includePhotos }),
          ...(updates.photosOnly === undefined
            ? {}
            : { audienceDocPhotosOnly: updates.photosOnly }),
          ...(updates.residentResponsibleOnly === undefined
            ? {}
            : { audienceDocResidentResponsibleOnly: updates.residentResponsibleOnly }),
          ...(updates.showAmounts === undefined
            ? {}
            : { audienceDocShowAmounts: updates.showAmounts }),
          ...(updates.showOnlyChangeOrderChanges === undefined
            ? {}
            : { audienceDocShowOnlyChangeOrderChanges: updates.showOnlyChangeOrderChanges }),
        },
      });
      trackEvent(ANALYTICS_EVENTS.audienceDocSettingsUpdated, {
        audienceDocId: audienceDoc.id,
        fields: Object.keys(updates).sort(),
      });
      return true;
    } catch (error) {
      setSettings(previousSettings);
      showErrorToast(error, 'save audience doc filters');
      return false;
    } finally {
      setPendingSettingsSaveCount(currentCount => currentCount - 1);
    }
  };

  const updateTaskAmount = (
    task: HistoricalArtifactTask,
    updatedAmount: number,
  ) => {
    if (!canManageShare) return;
    void scanDataMutators.setScan(scanRoot => getScanWithUpdatedHistoricalArtifactTaskAmount(scanRoot, task, updatedAmount))
      .then(() => trackEvent(ANALYTICS_EVENTS.scanUpdated, { scanId: audienceDoc.id, field: 'audience_doc_amount' }))
      .catch(error => showErrorToast(error, 'save audience doc amount'));
  };

  const copyAudienceDocLink = async () => {
    if (!canManageShare) return;
    try {
      const { url } = await createAudienceDocShareLink(audienceDoc.id);
      await navigator.clipboard.writeText(url);
      await refetch();
      trackEvent(hasActiveLink ? ANALYTICS_EVENTS.audienceDocShareLinkReplaced : ANALYTICS_EVENTS.audienceDocShareLinkCreated, {
        audienceDocId: audienceDoc.id,
      });
      showToast(
        hasActiveLink
          ? 'Copied a new audience doc link. The previous link is now inactive.'
          : 'Audience doc link copied to clipboard.',
        'success',
      );
    } catch (error) {
      showErrorToast(error, 'copy this audience doc link');
    }
  };

  const requestCopyAudienceDocLink = () =>
    hasActiveLink
      ? confirmAudienceDocShareLinkReplacement(copyAudienceDocLink)
      : copyAudienceDocLink();

  const renderAudienceDoc = (scopeLineItemIds?: ReadonlySet<string>) => {
    const isChangeOrder = !!scopeLineItemIds;
    const filteredScopeLineItems = filterAudienceDocLineItems(
      (reportData?.scopeLineItems ?? []).filter(
        scopeLineItem => !scopeLineItemIds || scopeLineItemIds.has(scopeLineItem.lineItemId),
      ),
      settings,
    );
    const baselineTaskAmounts = getHistoricalArtifactTaskBaselineAmounts(
      filterAudienceDocLineItems(
        (diffReportData?.scopeLineItems ?? []).filter(
          scopeLineItem => !scopeLineItemIds || scopeLineItemIds.has(scopeLineItem.lineItemId),
        ),
        settings,
      ),
    );
    const photoGalleryGroups = scanRoot
      ? buildHistoricalArtifactPhotoGalleryGroups(
        scanRoot,
        filteredScopeLineItems,
        !settings.residentResponsibleOnly,
      )
      : undefined;

    return (
      <HistoricalScopeArtifactDocument
        artifactTitle={audienceDoc.name || 'Audience Doc'}
        documentType={isChangeOrder ? 'changeOrder' : 'fullScope'}
        scan={audienceDoc}
        backLabel='All copies'
        emptyText='No tasks match this filter. Adjust the filters above to include more line items.'
        filterStrip={
          <div css={FILTER_STRIP_STYLE}>
            <div css={FILTER_CAPTION_STYLE}>
              <span css={FILTER_KICKER_STYLE}>Filters</span>
              <span css={FILTER_HELPER_STYLE}>
                These control what recipients see in the document below.
              </span>
            </div>
            {hasActiveLink && (
              <div css={LIVE_LINK_NOTE_STYLE} role='status'>
                <span css={LIVE_LINK_NOTE_LABEL_STYLE}>Live link active</span>
                <span>
                  {isSavingSettings
                    ? 'Saving recipient-facing changes…'
                    : 'Recipient-facing changes save immediately at the link you already shared. Copying a new link will replace it.'}
                </span>
              </div>
            )}
            <div css={FILTER_CONTROLS_STYLE}>
              {!settings.photosOnly && (
                <label css={FILTER_CHECKBOX_LABEL_STYLE}>
                  <input
                    aria-label='Include photos'
                    checked={settings.includePhotos}
                    css={FILTER_CHECKBOX_STYLE}
                    disabled={!canManageShare}
                    type='checkbox'
                    onChange={event => void updateSettings({ includePhotos: event.target.checked })}
                  />
                  <span css={FILTER_LABEL_STYLE}>Include photos</span>
                </label>
              )}
              <label css={FILTER_CHECKBOX_LABEL_STYLE}>
                <input
                  aria-label='Photos only'
                  checked={settings.photosOnly}
                  css={FILTER_CHECKBOX_STYLE}
                  disabled={!canManageShare}
                  type='checkbox'
                  onChange={event => void updateSettings({ photosOnly: event.target.checked })}
                />
                <span css={FILTER_LABEL_STYLE}>Photos only</span>
              </label>
              {!settings.photosOnly && (
                <label css={FILTER_CHECKBOX_LABEL_STYLE}>
                  <input
                    aria-label='Show dollar amounts'
                    checked={settings.showAmounts}
                    css={FILTER_CHECKBOX_STYLE}
                    disabled={!canManageShare}
                    type='checkbox'
                    onChange={event => void updateSettings({ showAmounts: event.target.checked })}
                  />
                  <span css={FILTER_LABEL_STYLE}>Show dollar amounts</span>
                </label>
              )}
              <label css={FILTER_CHECKBOX_LABEL_STYLE}>
                <input
                  aria-label={`${modifierLabels.tenantResponsibility} only`}
                  checked={settings.residentResponsibleOnly}
                  css={FILTER_CHECKBOX_STYLE}
                  disabled={!canManageShare}
                  type='checkbox'
                  onChange={event => void updateSettings({ residentResponsibleOnly: event.target.checked })}
                />
                <span css={FILTER_LABEL_STYLE}>{modifierLabels.tenantResponsibility} only</span>
              </label>
              {isChangeOrderCopy && !settings.photosOnly && (
                <label css={FILTER_CHECKBOX_LABEL_STYLE}>
                  <input
                    aria-label='Show only change order changes'
                    checked={settings.showOnlyChangeOrderChanges}
                    css={FILTER_CHECKBOX_STYLE}
                    disabled={!canManageShare}
                    type='checkbox'
                    onChange={event => void updateSettings({ showOnlyChangeOrderChanges: event.target.checked })}
                  />
                  <span css={FILTER_LABEL_STYLE}>Change Order Items Only</span>
                </label>
              )}
            </div>
          </div>
        }
        includePhotos={settings.includePhotos || settings.photosOnly}
        photosOnly={settings.photosOnly}
        photoGalleryGroups={photoGalleryGroups}
        scopeLineItems={filteredScopeLineItems}
        showAmounts={settings.photosOnly ? false : settings.showAmounts}
        canEditAmounts={canManageShare && !settings.photosOnly}
        baselineTaskAmounts={baselineTaskAmounts}
        onTaskAmountCommit={updateTaskAmount}
        toolbarMeta={
          <span css={TOOLBAR_META_STYLE}>
            Based on {basedOnTitle} · {hasActiveLink ? 'Expires' : 'No active link'}{' '}
            {shareLinkStatus?.expiresAt ? formatArtifactDate(shareLinkStatus.expiresAt) : ''}
          </span>
        }
        toolbarStatus={canManageShare
          ? (
            <button
              type='button'
              css={SHARE_BUTTON_STYLE}
              disabled={isCreatingAudienceDocShareLink || isSavingSettings}
              onClick={requestCopyAudienceDocLink}
            >
              {isSavingSettings
                ? 'Saving...'
                : isCreatingAudienceDocShareLink
                ? 'Copying...'
                : hasActiveLink
                ? 'Copy new link'
                : 'Copy link'}
            </button>
          )
          : <span css={TOOLBAR_META_STYLE}>Read-only</span>}
        onBack={onBack}
      />
    );
  };

  return settings.showOnlyChangeOrderChanges && isChangeOrderCopy
    ? (
      <ScanEditingContextProvider
        key={baseArtifact.id}
        scanId={baseArtifact.id}
        userHasAdminScanAccess={true}
      >
        <ChangeOrderScopeLineItemIds audienceDocScopeLineItems={reportData?.scopeLineItems ?? NO_SCOPE_LINE_ITEMS}>
          {scopeLineItemIds => renderAudienceDoc(scopeLineItemIds)}
        </ChangeOrderScopeLineItemIds>
      </ScanEditingContextProvider>
    )
    : renderAudienceDoc();
};
